"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FileSearch, SlidersHorizontal as Sliders, Globe, Star, Cpu, TrendingUp, ArrowRight, CircleCheck as CheckCircle2, UserCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const processSteps = [{
  step: "01",
  title: "Comprehensive SEO Audit",
  icon: FileSearch,
  description: "I perform a deep-dive technical, architecture, and competitor breakdown to identify exact bottlenecks suppressing your Google visibility.",
  deliverable: "Full technical roadmap & priority quick-wins"
}, {
  step: "02",
  title: "On-Page & Intent Optimization",
  icon: Sliders,
  description: "I restructure content, improve keyword targeting, and refine UX to align precisely with user search intent and conversion goals.",
  deliverable: "High-intent landing page & content blueprint"
}, {
  step: "03",
  title: "Citations & Geographic Relevance",
  icon: Globe,
  description: "I build hyper-targeted local and industry citation signals that strengthen your site's regional and topical domain trust.",
  deliverable: "Verified citation profile & authority links"
}, {
  step: "04",
  title: "Reviews & Conversion Trust Signals",
  icon: Star,
  description: "I optimize E-E-A-T factors and trust triggers so organic visitors confidently choose your services over competing brands.",
  deliverable: "Reputation setup & conversion rate optimization"
}, {
  step: "05",
  title: "Schema & AI Search Readiness",
  icon: Cpu,
  description: "I implement custom structured schema markup so Google Knowledge Graph and modern AI search engines accurately index your business.",
  deliverable: "JSON-LD schema & AI search optimization"
}, {
  step: "06",
  title: "Transparent Reporting & ROI",
  icon: TrendingUp,
  description: "No vanity metrics or agency jargon. You receive clear monthly performance reports and direct strategic reviews with me.",
  deliverable: "Revenue-focused tracking & monthly strategy call"
}];
export default function Methodology() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".process-card", {
        y: 30
      });
      gsap.to(".process-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".process-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section id="methodologyprocess" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div ref={container} className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
        
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-sm bg-secondary text-secondary-foreground border border-accent/20">
            Methodology &amp; Workflow
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            My Proven Step-by-Step SEO Process
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans leading-relaxed">
            Sustainable search growth requires systematic execution. When you hire me, you work directly with me at every stage — no account managers, no outsourcing.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-accent/10 via-accent/40 to-accent/10 -translate-y-12 pointer-events-none" aria-hidden="true" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
            {processSteps.map((item, i) => {
            const IconComponent = item.icon;
            return <div key={item.step} data-index={i} className="process-card flex" style={{
              opacity: 0
            }}>
                  <Card className="flex flex-col justify-between w-full border-border/80 shadow-sm hover:shadow-md transition-shadow duration-300 rounded-xl bg-card overflow-hidden group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground font-serif font-bold text-lg flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform duration-300">
                          {item.step}
                        </div>

                        <div className="w-10 h-10 rounded-lg bg-secondary text-primary flex items-center justify-center">
                          <IconComponent className="w-5 h-5" />
                        </div>
                      </div>

                      <CardTitle className="text-xl font-semibold font-serif text-card-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
                      <CardDescription className="text-sm text-muted-foreground font-sans leading-relaxed">
                        {item.description}
                      </CardDescription>

                      <div className="pt-3 border-t border-border/60 flex items-start gap-2 text-xs font-medium text-foreground">
                        <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                        <span>{item.deliverable}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-16 lg:mt-24 rounded-2xl bg-muted/60 border border-border p-8 md:p-10 text-center lg:text-left flex flex-col lg:flex-row items-center justify-between gap-8 shadow-xs">
          <div className="max-w-2xl space-y-2">
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
              <UserCheck className="w-4 h-4 text-accent" />
              <span>Direct Expert Access Guarantee</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              Ready for an SEO strategy built specifically for your business?
            </h3>
            <p className="text-muted-foreground text-sm md:text-base font-sans">
              Book a direct consultation with me, Yassine Essaadi. I will evaluate your current rankings and map out a clear growth path.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full lg:w-auto">
            <Button size="lg" asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="w-full sm:w-auto border-border text-foreground hover:bg-accent/10">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}